import React, { useState } from 'react';
import { X, Phone, Send, Layers, Shield, ChevronRight, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Privacy Modal ──────────────────────────────────────────────────

const PrivacyModal = ({ onClose }) => (
  <div
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
    onClick={onClose}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{ background: '#17212b', borderRadius: '16px', maxWidth: '400px', width: '100%', maxHeight: '72vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
      className="tg-scrollbar"
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 20px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: '17px' }}>Политика конфиденциальности</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#708499', padding: 4, display: 'flex', alignItems: 'center' }}>
          <X size={18} />
        </button>
      </div>
      <div style={{ padding: '16px 20px 24px' }}>
        {[
          ['Общие положения', 'Данный сайт является персональным портфолио разработчика Telegram-ботов. Сайт носит исключительно информационный и демонстрационный характер.'],
          ['Сбор данных', 'Мы не собираем, не храним и не обрабатываем персональные данные посетителей. Сайт не использует файлы cookie, аналитику или системы трекинга.'],
          ['Форма заявок', 'Данные, отправленные через форму заявок, используются исключительно для связи с вами. Ваши контактные данные не передаются третьим лицам.'],
          ['Внешние переходы', 'При нажатии кнопок «Запустить демо-бота» или «Написать» вы переходите на внешний сервис Telegram.'],
          ['Контакты', 'По всем вопросам: @extremum7'],
        ].map(([title, text], i) => (
          <div key={i} style={{ marginBottom: '18px' }}>
            <p style={{ color: '#5288c1', fontWeight: 600, fontSize: '12px', margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{title}</p>
            <p style={{ color: '#c5d9eb', fontSize: '14px', lineHeight: 1.65, margin: 0 }}>{text}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── Request Form Modal ──────────────────────────────────────────

const REQUEST_TYPES = [
  { key: 'order', label: 'Заказать разработку', fieldLabel: 'Опишите задачу' },
  { key: 'question', label: 'Вопрос', fieldLabel: 'Ваш вопрос' },
  { key: 'suggestion', label: 'Предложение', fieldLabel: 'Ваше предложение' },
];

const inputStyle = {
  width: '100%',
  background: '#212d3b',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  padding: '10px 14px',
  color: '#ffffff',
  fontSize: '15px',
  outline: 'none',
  caretColor: '#5288c1',
  boxSizing: 'border-box',
  fontFamily: "'Inter', sans-serif",
};

const RequestModal = ({ onClose }) => {
  const [type, setType] = useState('order');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const currentType = REQUEST_TYPES.find(t => t.key === type);
  const canSubmit = name.trim() && message.trim() && contact.trim() && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) { setError('Заполните все поля'); return; }
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${BACKEND_URL}/api/submit-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: currentType.label, name: name.trim(), message: message.trim(), contact: contact.trim() }),
      });
      if (resp.ok) {
        setSent(true);
      } else {
        const data = await resp.json();
        setError(data.detail || 'Ошибка отправки');
      }
    } catch {
      setError('Ошибка соединения. Напишите напрямую: @extremum7');
    }
    setLoading(false);
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#17212b', borderRadius: '16px', maxWidth: '420px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
        className="tg-scrollbar"
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 20px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: '17px' }}>Оставить заявку</div>
            <div style={{ color: '#708499', fontSize: '13px', marginTop: '2px' }}>Задать вопрос</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#708499', padding: 4, display: 'flex' }}>
            <X size={18} />
          </button>
        </div>

        {sent ? (
          /* Success screen */
          <div style={{ padding: '32px 20px', textAlign: 'center' }}>
            <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(77,205,94,0.15)', border: '2px solid #4dcd5e', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <span style={{ fontSize: '26px' }}>✓</span>
            </div>
            <p style={{ color: '#fff', fontWeight: 600, fontSize: '18px', margin: '0 0 8px' }}>Заявка отправлена!</p>
            <p style={{ color: '#708499', fontSize: '14px', margin: '0 0 24px', lineHeight: 1.5 }}>Мы ответим на ваш контакт в ближайшее время.</p>
            <button
              onClick={onClose}
              style={{ background: '#5288c1', color: '#fff', border: 'none', borderRadius: '12px', padding: '12px 32px', cursor: 'pointer', fontSize: '15px', fontWeight: 600 }}
            >
              Закрыть
            </button>
          </div>
        ) : (
          /* Form */
          <div style={{ padding: '16px 20px 20px' }}>
            {/* Type selector */}
            <p style={{ color: '#708499', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.6px', margin: '0 0 8px', fontWeight: 600 }}>Тип заявки</p>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {REQUEST_TYPES.map(t => (
                <button
                  key={t.key}
                  onClick={() => setType(t.key)}
                  style={{
                    padding: '7px 14px',
                    borderRadius: '20px',
                    border: `1.5px solid ${type === t.key ? '#5288c1' : 'rgba(255,255,255,0.1)'}`,
                    background: type === t.key ? 'rgba(82,136,193,0.18)' : 'transparent',
                    color: type === t.key ? '#5288c1' : '#708499',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: type === t.key ? 600 : 400,
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Name */}
            <p style={{ color: '#708499', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.6px', margin: '0 0 8px', fontWeight: 600 }}>Имя</p>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ваше имя"
              style={{ ...inputStyle, marginBottom: '16px' }}
            />

            {/* Message */}
            <p style={{ color: '#708499', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.6px', margin: '0 0 8px', fontWeight: 600 }}>{currentType.fieldLabel}</p>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={currentType.fieldLabel + '...'}
              rows={4}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '88px', marginBottom: '16px' }}
            />

            {/* Contact */}
            <p style={{ color: '#708499', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.6px', margin: '0 0 4px', fontWeight: 600 }}>Контакт</p>
            <p style={{ color: '#4a5d72', fontSize: '12px', margin: '0 0 8px' }}>Telegram, ссылка, номер или почта</p>
            <input
              value={contact}
              onChange={e => setContact(e.target.value)}
              placeholder="@username, +7..., email@..."
              style={{ ...inputStyle, marginBottom: error ? '10px' : '20px' }}
            />

            {error && (
              <p style={{ color: '#e17076', fontSize: '13px', margin: '0 0 14px', lineHeight: 1.4 }}>{error}</p>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{
                width: '100%',
                background: canSubmit ? '#5288c1' : '#2b5278',
                color: canSubmit ? '#fff' : 'rgba(255,255,255,0.4)',
                border: 'none',
                borderRadius: '12px',
                padding: '13px',
                cursor: canSubmit ? 'pointer' : 'default',
                fontSize: '15px',
                fontWeight: 600,
                transition: 'background 0.2s, color 0.2s',
              }}
            >
              {loading ? 'Отправка...' : 'Отправить'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Menu Item ────────────────────────────────────────────────────────

const MenuItem = ({ icon: Icon, label, subtitle, onClick, disabled, badge }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '14px',
        width: '100%', padding: '12px 20px',
        background: hovered ? 'rgba(255,255,255,0.05)' : 'transparent',
        border: 'none', cursor: disabled ? 'default' : 'pointer',
        textAlign: 'left', transition: 'background 0.15s ease',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(82,136,193,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={18} color="#5288c1" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#fff', fontSize: '15px', fontWeight: 500 }}>{label}</div>
        {subtitle && <div style={{ color: '#708499', fontSize: '12px', marginTop: '2px' }}>{subtitle}</div>}
      </div>
      {badge && (
        <span style={{ background: '#2b5278', color: '#a8c8e8', borderRadius: '10px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
          {badge}
        </span>
      )}
      {!disabled && !badge && <ChevronRight size={16} color="#708499" />}
    </button>
  );
};

// ── Side Menu ─────────────────────────────────────────────────────────

const SideMenu = ({ isOpen, onClose, onOpenContacts }) => {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);

  return (
    <>
      {privacyOpen && <PrivacyModal onClose={() => setPrivacyOpen(false)} />}
      {requestOpen && <RequestModal onClose={() => setRequestOpen(false)} />}

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 400,
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'opacity 0.25s ease',
        }}
      />

      {/* Slide-in panel */}
      <div style={{
        position: 'fixed', top: 0, bottom: 0, left: 0,
        width: '300px',
        background: '#17212b',
        zIndex: 500,
        display: 'flex', flexDirection: 'column',
        boxShadow: '4px 0 32px rgba(0,0,0,0.5)',
        transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        {/* Profile header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '20px 16px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#5288c1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '16px', flexShrink: 0 }}>
            АП
          </div>
          <div style={{ flex: 1, marginLeft: '12px', minWidth: 0 }}>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: '16px' }}>Александр</div>
            <a
              href="https://t.me/extremum7"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#5288c1', fontSize: '13px', marginTop: '2px', display: 'block', textDecoration: 'none' }}
            >
              @extremum7
            </a>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            <X size={18} color="#708499" />
          </button>
        </div>

        {/* Menu items */}
        <div style={{ flex: 1, paddingTop: '8px', overflowY: 'auto' }} className="tg-scrollbar">
          <MenuItem
            icon={Phone}
            label="Контакты"
            subtitle="Информация и ссылки"
            onClick={() => { onOpenContacts(); onClose(); }}
          />
          <MenuItem
            icon={Send}
            label="Связаться"
            subtitle="Написать в Telegram"
            onClick={() => window.open('https://t.me/extremum7', '_blank')}
          />
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)', margin: '4px 0' }} />
          <MenuItem
            icon={FileText}
            label="Оставить заявку"
            subtitle="Задать вопрос"
            onClick={() => { setRequestOpen(true); onClose(); }}
          />
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)', margin: '4px 0' }} />
          <MenuItem
            icon={Layers}
            label="Сменить портфолио"
            subtitle="Другие разделы"
            disabled
            badge="Скоро"
          />
          <MenuItem
            icon={Shield}
            label="Политика конфиденциальности"
            onClick={() => setPrivacyOpen(true)}
          />
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.05)', color: '#708499', fontSize: '12px', textAlign: 'center' }}>
          Telegram Bot Portfolio · 2026
        </div>
      </div>
    </>
  );
};

export default SideMenu;
